import { useState, type ReactNode } from 'react';
import { Link, NavLink, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  INVOICE_BASIS_LABELS,
  INVOICE_STATUS_LABELS,
  addMonths,
  canOpenContract,
  daysBetween,
  formatDate,
  invoicePaymentState,
  toIsoDate,
  type InvoiceStatus,
  type Paginated,
} from '@codepass/shared';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import { contractPath, invoicePath } from '@/lib/paths';
import { invoiceCurrency, invoiceCustomerName } from '@/lib/invoice';
import {
  Alert,
  Badge,
  Button,
  Card,
  Input,
  Select,
  Skeleton,
  Table,
  Td,
  Th,
  Tr,
} from '@/components/ui';
import {
  EmptyState,
  InvoiceStatusBadge,
  Money,
  PageHeader,
  Pagination,
  PermittedLink,
  RefreshButton,
} from '@/components/domain';
import { cn } from '@/lib/cn';
import type { InvoiceListItem } from '@/types';

const LIMIT = 50;

/**
 * Hvilke statusser hoerer paa hvilket faneblad.
 *
 * Codepass laver kladderne, og bogholderiet gennemgaar, bogfoerer og sender dem i Business
 * Central. Fakturaudkast er derfor BC's kladder, og Historik er det, BC har bogfoert - sendt til
 * kunden, og evt. annulleret bagefter med en kreditnota.
 *
 * Codepass' egen godkendelse - Godkend, Send til BC, Annullér - blev fjernet 06-10-2026. Den
 * gentog et skridt, der alligevel skete i BC.
 */
const DRAFT_STATUSES: readonly InvoiceStatus[] = ['draft'];
const HISTORY_STATUSES: readonly InvoiceStatus[] = ['posted', 'cancelled'];

/**
 * The tabs under Fakturering - only those the user's roles may open, each its own permission on
 * /admin/roller. Exported for Subscriptions.tsx and PartnerCommission.tsx, the third and fourth
 * tab.
 */
export function SubNav() {
  const { can } = useAuth();
  const pages = [
    can('invoices.drafts.view') && { to: '/fakturering', label: 'Fakturaudkast' },
    can('invoices.history.view') && { to: '/fakturering/historik', label: 'Historik' },
    can('subscriptions.view') && {
      to: '/fakturering/abonnementer',
      label: 'Abonnementsoversigt',
    },
    can('partner_commission.view') && { to: '/fakturering/provision', label: 'Provision' },
  ].filter((page): page is { to: string; label: string } => Boolean(page));

  return (
    <nav className="mb-4 flex gap-1 border-b border-line">
      {pages.map((page) => (
        <NavLink
          key={page.to}
          to={page.to}
          end
          className={({ isActive }) =>
            cn(
              '-mb-px border-b-2 px-3 py-2 text-sm',
              isActive
                ? 'border-brand font-medium text-brand-800'
                : 'border-transparent text-ink-muted hover:text-ink',
            )
          }
        >
          {page.label}
        </NavLink>
      ))}
    </nav>
  );
}

/**
 * The period on both tabs: `from` and `to` in the address, both days included.
 *
 * **The last month by default** - from the same date a month ago to today. In the address and
 * not in state, so a link or a reload keeps the period. An emptied field is kept as empty, which
 * means no bound on that side.
 */
function usePeriod(onChange: () => void) {
  const [params, setParams] = useSearchParams();
  const today = toIsoDate(new Date());
  const defaults = { from: addMonths(today, -1), to: today };

  const from = params.get('from') ?? defaults.from;
  const to = params.get('to') ?? defaults.to;

  const update = (changes: Record<string, string | null>): void => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null) next.delete(key);
      else next.set(key, value);
    }
    setParams(next, { replace: true });
    // Et aendret filter goer den aktuelle side meningsloes - start forfra.
    onChange();
  };

  return {
    from,
    to,
    params,
    update,
    isDefault: from === defaults.from && to === defaults.to,
    reset: () => update({ from: null, to: null }),
  };
}

function PeriodFilter({
  label,
  period,
  total,
  children,
}: {
  /** What the dates are - `Fakturadato` or `Sendt`. */
  label: string;
  period: ReturnType<typeof usePeriod>;
  /** Invoices in the period, once they are loaded. */
  total: number | null;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3 border-b border-line p-3">
      <div className="w-full sm:w-40">
        <span className="field-label">{label} fra</span>
        <Input
          type="date"
          value={period.from}
          max={period.to || undefined}
          onChange={(event) => period.update({ from: event.target.value })}
        />
      </div>
      <div className="w-full sm:w-40">
        <span className="field-label">{label} til</span>
        <Input
          type="date"
          value={period.to}
          min={period.from || undefined}
          onChange={(event) => period.update({ to: event.target.value })}
        />
      </div>
      {children}
      {!period.isDefault ? (
        <Button size="sm" variant="ghost" onClick={period.reset}>
          Seneste måned
        </Button>
      ) : null}
      {total !== null ? (
        <span className="ml-auto self-center text-xs text-ink-muted">
          {total === 1 ? '1 faktura' : `${total} fakturaer`} i perioden
        </span>
      ) : null}
    </div>
  );
}

function InvoiceTable({ rows, sent }: { rows: InvoiceListItem[]; sent: boolean }) {
  const today = toIsoDate(new Date());
  /* An invoice names its contracts, not their kind - see `canOpenContract`. */
  const opensContract = canOpenContract(useAuth().user, null);

  return (
    <Table>
      <thead>
        <tr>
          <Th>Faktura</Th>
          <Th>Kontrakt</Th>
          <Th className="w-[110px]">Grundlag</Th>
          <Th className="w-[140px] text-right">Ekskl. moms</Th>
          <Th className="w-[140px] text-right">Inkl. moms</Th>
          <Th className="w-[150px]">Forfaldsdato</Th>
          <Th className="w-[130px]">Status</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((invoice) => {
          const daysToDue = invoice.due_date ? daysBetween(today, invoice.due_date) : null;
          return (
            <Tr key={invoice.id}>
              <Td>
                <Link to={invoicePath(invoice)} className="font-medium hover:underline">
                  {invoice.bc_invoice_id ?? invoice.invoice_no_proposal ?? 'Kladde'}
                </Link>
                {/* The date the tab filters on, so the list and the filter say the same. */}
                <span className="block text-xs text-ink-muted">
                  {sent
                    ? `Sendt ${formatDate(invoice.posting_date ?? null)}`
                    : formatDate(invoice.created_at)}
                  {invoice.generated_by === 'agent' ? ' · automatisk' : ''}
                </span>
              </Td>
              <Td>
                {/*
                 * **Alle** aftaler fakturaen daekker, hver med sit eget link. Én faktura kan
                 * daekke flere - se `parseInvoiceReference` - og at vise den foerste alene ville
                 * skjule, hvad der ellers blev faktureret paa den.
                 *
                 * En BC-faktura har ingen aftale. Ingen tom linje og ingen doedt link der peger
                 * paa /kontrakter/null - kunden staar der stadig, for den kender BC.
                 */}
                {(invoice.contracts ?? (invoice.contract ? [invoice.contract] : [])).length > 0 ? (
                  <span className="flex flex-col gap-0.5">
                    {(invoice.contracts ?? (invoice.contract ? [invoice.contract] : [])).map(
                      (contract) => (
                        <PermittedLink
                          key={contract.id}
                          to={contractPath(contract)}
                          allowed={opensContract}
                          className="text-sm hover:underline"
                        >
                          {contract.title}
                        </PermittedLink>
                      ),
                    )}
                  </span>
                ) : (
                  <span className="text-sm text-ink-muted">—</span>
                )}
                <span className="block text-xs text-ink-muted">
                  {invoiceCustomerName(invoice) ?? '—'}
                </span>
              </Td>
              <Td className="text-xs">
                {invoice.invoice_basis ? INVOICE_BASIS_LABELS[invoice.invoice_basis] : '—'}
              </Td>
              <Td className="text-right">
                <Money value={invoice.amount_excl_vat} currency={invoiceCurrency(invoice)} />
              </Td>
              <Td className="text-right">
                <Money value={invoice.amount_incl_vat} currency={invoiceCurrency(invoice)} />
              </Td>
              <Td className="text-xs tabular">
                {formatDate(invoice.due_date)}
                {/* A paid invoice is not overdue - its payment date is under Status. */}
                {daysToDue !== null &&
                invoice.status !== 'draft' &&
                invoicePaymentState({ ...invoice }) !== 'paid' ? (
                  <Badge tone={daysToDue < 0 ? 'danger' : 'neutral'} className="ml-1">
                    {daysToDue < 0 ? `${Math.abs(daysToDue)} d. over` : `${daysToDue} d.`}
                  </Badge>
                ) : null}
              </Td>
              <Td>
                <InvoiceStatusBadge status={invoice.status} payment={invoice} />
              </Td>
            </Tr>
          );
        })}
      </tbody>
    </Table>
  );
}

export function InvoicesPage() {
  const { can } = useAuth();
  /* 'Se fakturaudkast' uden 'Lav fakturaudkast': listen uden knappen. */
  const canCreate = can('invoices.create');
  const [offset, setOffset] = useState(0);
  const period = usePeriod(() => setOffset(0));

  const { data, isLoading, error } = useQuery({
    queryKey: ['invoices', 'drafts', period.from, period.to, offset],
    queryFn: () =>
      api.get<Paginated<InvoiceListItem>>('/invoices', {
        status: DRAFT_STATUSES.join(','),
        date: 'invoice',
        from: period.from,
        to: period.to,
        limit: LIMIT,
        offset,
      }),
  });

  return (
    <>
      <PageHeader
        title="Fakturering"
        description="Kladderne i Business Central. Bogholderiet gennemgår og sender dem derfra."
        actions={
          <>
            {/* Opdateringen staar til venstre for den primaere handling. */}
            <RefreshButton prefixes={['invoices']} />
            {canCreate ? (
              <Link to="/fakturering/ny">
                <Button size="sm">Fakturér sprint</Button>
              </Link>
            ) : null}
          </>
        }
      />
      <SubNav />

      <Card>
        <PeriodFilter label="Fakturadato" period={period} total={data?.total ?? null} />

        {isLoading ? (
          <Skeleton className="m-4 h-32" />
        ) : error ? (
          <Alert tone="error" className="m-4">
            {error instanceof ApiError ? error.message : 'Kunne ikke hente fakturaer.'}
          </Alert>
        ) : (data?.data.length ?? 0) === 0 ? (
          <EmptyState
            title="Ingen kladder i perioden"
            description={
              canCreate
                ? 'Vælg en længere periode, eller opret en kladde ved at fakturere et sprint.'
                : 'Vælg en længere periode.'
            }
            action={
              canCreate ? (
                <Link to="/fakturering/ny">
                  <Button size="sm">Fakturér sprint</Button>
                </Link>
              ) : undefined
            }
          />
        ) : (
          <>
            <InvoiceTable rows={data?.data ?? []} sent={false} />
            <Pagination
              total={data?.total ?? 0}
              limit={LIMIT}
              offset={offset}
              onChange={setOffset}
            />
          </>
        )}
      </Card>
    </>
  );
}

export function InvoiceHistoryPage() {
  const [offset, setOffset] = useState(0);
  const period = usePeriod(() => setOffset(0));
  const status = period.params.get('status') ?? '';

  const { data, isLoading, error } = useQuery({
    queryKey: ['invoices', 'history', status, period.from, period.to, offset],
    queryFn: () =>
      api.get<Paginated<InvoiceListItem>>('/invoices', {
        status: status || HISTORY_STATUSES.join(','),
        date: 'posting',
        from: period.from,
        to: period.to,
        limit: LIMIT,
        offset,
      }),
  });

  return (
    <>
      <PageHeader
        title="Faktureringshistorik"
        description="Fakturaer bogholderiet har sendt fra Business Central. Sendt er bogføringsdatoen."
        actions={<RefreshButton prefixes={['invoices']} />}
      />
      <SubNav />

      <Card>
        <PeriodFilter label="Sendt" period={period} total={data?.total ?? null}>
          <div className="w-full sm:w-44">
            <span className="field-label">Status</span>
            <Select
              value={status}
              onChange={(event) => period.update({ status: event.target.value || null })}
            >
              <option value="">Alle sendte</option>
              {HISTORY_STATUSES.map((option) => (
                <option key={option} value={option}>
                  {INVOICE_STATUS_LABELS[option]}
                </option>
              ))}
            </Select>
          </div>
        </PeriodFilter>

        {isLoading ? (
          <Skeleton className="m-4 h-32" />
        ) : error ? (
          <Alert tone="error" className="m-4">
            {error instanceof ApiError ? error.message : 'Kunne ikke hente fakturaer.'}
          </Alert>
        ) : (data?.data.length ?? 0) === 0 ? (
          <EmptyState
            title="Ingen fakturaer sendt i perioden"
            description="Vælg en længere periode for at se ældre fakturaer."
          />
        ) : (
          <>
            <InvoiceTable rows={data?.data ?? []} sent />
            <Pagination
              total={data?.total ?? 0}
              limit={LIMIT}
              offset={offset}
              onChange={setOffset}
            />
          </>
        )}
      </Card>
    </>
  );
}
